import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DisciplineCasesDetailDialog = ({ open, onOpenChange }: Props) => {
  const { data: cases = [], isLoading } = useQuery({
    queryKey: ["discipline-cases-detail"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("discipline_cases")
        .select("id, description, status, severity, category, incident_date, student_id")
        .order("created_at", { ascending: false })
        .limit(100);

      if (!rows?.length) return [];

      const studentIds = [...new Set(rows.map((r) => r.student_id))];
      const { data: students } = await supabase.from("students").select("id, first_name, last_name, student_number").in("id", studentIds);
      const map = new Map((students ?? []).map((s) => [s.id, `${s.first_name} ${s.last_name} (${s.student_number})`]));

      return rows.map((r) => ({ ...r, student_name: map.get(r.student_id) ?? "Unknown" }));
    },
    enabled: open,
  });

  const openCases = cases.filter((c) => c.status === "open" || c.status === "investigation");
  const hearings = cases.filter((c) => c.status === "hearing");
  const resolved = cases.filter((c) => c.status === "resolved");

  const handleExport = () => {
    const headers = ["Description", "Student", "Category", "Severity", "Status", "Incident Date"];
    const rows = cases.map((c) => [c.description, c.student_name, c.category ?? "", c.severity ?? "", c.status, c.incident_date ?? ""]);
    exportToCsv("discipline-cases.csv", headers, rows);
  };

  const renderList = (items: typeof cases) => (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      {isLoading && <p className="text-sm text-muted-foreground text-center py-4">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">No cases found</p>
      )}
      {items.map((c) => (
        <div key={c.id} className="flex items-start justify-between p-3 rounded-lg border bg-card">
          <div className="space-y-1 min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{c.description}</p>
            <p className="text-xs text-muted-foreground truncate">{c.student_name}</p>
            <p className="text-xs text-muted-foreground">
              {c.category ?? "General"} · {c.incident_date ? format(new Date(c.incident_date), "MMM d, yyyy") : "N/A"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 ml-2 shrink-0">
            <Badge variant={c.severity === "major" ? "destructive" : "secondary"}>{c.severity}</Badge>
            <Badge variant="outline">{c.status}</Badge>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex flex-row items-center justify-between gap-2">
          <DialogTitle>Discipline Cases</DialogTitle>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={cases.length === 0} className="mr-8 shrink-0">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </DialogHeader>
        <Tabs defaultValue="open">
          <TabsList className="w-full">
            <TabsTrigger value="open" className="flex-1">Open ({openCases.length})</TabsTrigger>
            <TabsTrigger value="hearing" className="flex-1">Hearings ({hearings.length})</TabsTrigger>
            <TabsTrigger value="resolved" className="flex-1">Resolved ({resolved.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="open">{renderList(openCases)}</TabsContent>
          <TabsContent value="hearing">{renderList(hearings)}</TabsContent>
          <TabsContent value="resolved">{renderList(resolved)}</TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
